import React from 'react';
import styles from './index.module.less';
import {HTTP_STATUS, ERROR_MESSAGES} from '@/constants';
import StatusView from '@/components/common/StatusView';
import {captureError} from '@/logger/weirwood';

interface Props {
    children: React.ReactNode;
    /** global: 全屏兜底（App 根层）；page: 内容区降级（页面/路由层） */
    level?: 'global' | 'page';
    /** 完全自定义 fallback UI */
    fallback?: React.ReactNode;
    /** 错误上报回调（接入 Sentry 等监控时使用） */
    onError?: (error: Error, info: React.ErrorInfo) => void;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

class ErrorBoundary extends React.Component<Props, State> {
    state: State = {hasError: false, error: null};

    static getDerivedStateFromError(error: Error): State {
        return {hasError: true, error};
    }

    componentDidCatch(error: Error, info: React.ErrorInfo) {
        captureError(error, {
            source: 'react-error-boundary',
            level: this.props.level,
            componentStack: info.componentStack,
        });

        this.props.onError?.(error, info);
        console.error('[ErrorBoundary]', error.message, info.componentStack);

        // Chunk 加载失败（发版时旧 chunk 被删除）→ 自动刷新一次获取最新资源
        if (error.name === 'ChunkLoadError' || error.message.includes('Loading chunk')) {
            window.location.reload();
        }
    }

    handleRetry = () => {
        this.setState({hasError: false, error: null});
    };

    render() {
        const {hasError, error} = this.state;
        const {children, fallback, level = 'page'} = this.props;

        if (!hasError) return children;
        if (fallback) return fallback;

        // 403 无权限：渲染专属无权限视图
        const is403 =
            (this.state.error as unknown as {response?: {status?: number}})?.response?.status ===
            HTTP_STATUS.FORBIDDEN;
        if (is403) {
            return (
                <div className={styles.globalError}>
                    <StatusView status="error" title={ERROR_MESSAGES.PERMISSION_NO_ACCESS} />
                </div>
            );
        }

        if (level === 'global') {
            return (
                <div className={styles.globalError}>
                    <StatusView
                        status="error"
                        title="页面出现了问题"
                        description={error?.message ?? '未知错误，请刷新页面后重试'}
                        onRetry={() => window.location.reload()}
                        retryText="重试"
                    />
                </div>
            );
        }

        return (
            <div className={styles.pageError}>
                <StatusView
                    status="error"
                    title={ERROR_MESSAGES.LOAD_FAILED_MESSAGE}
                    description={error?.message ?? '未知错误'}
                    onRetry={this.handleRetry}
                    retryText="重试"
                />
            </div>
        );
    }
}

export default ErrorBoundary;
